import type { Container } from "pixi.js";
import type { PresentationTaskRunner } from "../../runner/PresentationTaskRunner";
import type { SymbolWinEffect, SymbolWinAnimationRequest } from "./SymbolWinEffect";
import type { ScalePulseTuning } from "./ScalePulseTuning";

/** Per-cell pulse-tuning selector, chosen by the winning symbol's id (e.g. L vs H). */
export type ScalePulseTuningResolver = (symbolId: string) => ScalePulseTuning;

interface ActivePulse {
  /** The node's rest scale, captured before the first pulse; restored on stopAll. */
  base: { x: number; y: number };
  /** Bumped per play() on this node; an in-flight chain whose token is stale stops writing. */
  gen: number;
}

/**
 * Default symbol-win animation: a squash-and-stretch "pulse". The winning cell SQUEEZES, STRETCHES
 * up, HOLDS, then SETTLES back to base — one sequential scale chain on engine time.
 * `uniformScale` makes X track Y; default is Y-only. `{ once: true }` pulses once per session.
 */
export class ScalePulseSymbolWinEffect implements SymbolWinEffect {
  private readonly resolveTuning: ScalePulseTuningResolver;
  private readonly taskRunner: PresentationTaskRunner;
  private readonly once: boolean;
  private readonly active: Map<Container, ActivePulse> = new Map();
  private generation = 0;

  public constructor(
    tuning: ScalePulseTuning | ScalePulseTuningResolver,
    taskRunner: PresentationTaskRunner,
    options: { once?: boolean } = {},
  ) {
    this.resolveTuning = typeof tuning === "function" ? tuning : () => tuning;
    this.taskRunner = taskRunner;
    this.once = options.once ?? false;
  }

  public play(request: SymbolWinAnimationRequest): void {
    const node = request.cellNode;
    if (!node || node.destroyed) return;

    const existing = this.active.get(node);
    if (this.once && existing) return;
    const base = existing ? existing.base : { x: node.scale.x, y: node.scale.y };

    const gen = ++this.generation;
    this.active.set(node, { base, gen });
    void this.run(node, base, gen, this.resolveTuning(request.symbolId));
  }

  public stopAll(): void {
    for (const [node, pulse] of this.active) {
      if (node && !node.destroyed) node.scale.set(pulse.base.x, pulse.base.y);
    }
    this.active.clear();
  }

  private async run(node: Container, base: { x: number; y: number }, gen: number, t: ScalePulseTuning): Promise<void> {
    const isCurrent = (): boolean => !node.destroyed && this.active.get(node)?.gen === gen;
    const setY = (y: number): void => {
      if (!isCurrent()) return;
      if (t.uniformScale) {
        const factor = y / base.y;
        node.scale.set(base.x * factor, y);
      } else {
        node.scale.set(base.x, y);
      }
    };

    const yBase = base.y;
    const ySqueeze = base.y * t.squeezeFactor;
    const yStretch = base.y * t.scaleFactor;

    const inDelaySec = t.scaleInStartDelayMilliseconds / 1000;
    if (inDelaySec > 0) {
      await this.taskRunner.sleep(inDelaySec);
      if (!isCurrent()) return;
    }

    let fromY = yBase;
    if (t.squeezeFactor < 1) {
      await this.taskRunner.tweenProgress(
        t.squeezeSeconds,
        (t01) => setY(fromY + (ySqueeze - fromY) * t01),
        undefined,
        { easing: t.squeezeEasing },
      );
      if (!isCurrent()) return;
      fromY = ySqueeze;
    }

    const stretchFrom = fromY;
    await this.taskRunner.tweenProgress(
      t.scaleUpSeconds,
      (t01) => setY(stretchFrom + (yStretch - stretchFrom) * t01),
      undefined,
      { easing: t.scaleUpEasing },
    );
    if (!isCurrent()) return;

    const holdSec = t.scaleHoldSeconds ?? 0;
    if (holdSec > 0) {
      await this.taskRunner.sleep(holdSec);
      if (!isCurrent()) return;
    }

    await this.taskRunner.tweenProgress(
      t.scaleDownSeconds,
      (t01) => setY(yStretch + (yBase - yStretch) * t01),
      undefined,
      { easing: t.scaleDownEasing },
    );
  }
}
